/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { Item } from "~/types";

export type ListId = "SELECTED_ITEMS";

export type Items = Record<ListId, Item[]>;

export function useComponentsSelector(initialSelectedItems: Item[]) {
  const [items, setItems] = useState<Items>(() => ({
    ["SELECTED_ITEMS"]: initialSelectedItems,
  }));

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    const moveBetweenSameList = source.droppableId === destination.droppableId;

    if (moveBetweenSameList) {
      const newItems = reorder(
        items[source.droppableId as ListId],
        source.index,
        destination.index
      );

      setItems((prevItems) => ({
        ...prevItems,
        ...(source.droppableId === "SELECTED_ITEMS"
          ? { ["SELECTED_ITEMS"]: newItems }
          : {}),
      }));

      return;
    }

    const { sourceItems, destinationItems } = moveBetween({
      sourceItems: items[source.droppableId as ListId],
      destinationItems: items[destination.droppableId as ListId],
      sourceIndex: source.index,
      destinationIndex: destination.index,
    });

    setItems({
      ["SELECTED_ITEMS"]:
        source.droppableId === "SELECTED_ITEMS"
          ? sourceItems
          : destinationItems,
    });
  };

  const handleDelete = (item: Item) => {
    setItems((prevItems) => ({
      ["SELECTED_ITEMS"]: prevItems.SELECTED_ITEMS.filter(
        (prevItem) => prevItem.id !== item.id
      ),
    }));
  };

  const handleShowItems = () => {
    setItems((prevItems) => ({
      ...prevItems,
      ["SELECTED_ITEMS"]: initialSelectedItems,
    }));
  };

  const handleShowSearch = useCallback((result: Item[]) => {
    setItems((prevItems) => ({
      ...prevItems,
      ["SELECTED_ITEMS"]: result,
    }));
  }, []);

  return {
    items,
    handleShowItems,
    handleDragEnd,
    handleDelete,
    handleShowSearch,
  };
}

export function reorder<T>(
  list: T[],
  startIndex: number,
  endIndex: number
): T[] {
  const filteredList = list.filter((_ignored, index) => index !== startIndex);
  return [
    ...filteredList.slice(0, endIndex),
    list[startIndex],
    ...filteredList.slice(endIndex),
  ];
}

function moveBetween<T extends { id: string }>({
  sourceItems,
  destinationItems,
  sourceIndex,
  destinationIndex,
}: {
  sourceItems: T[];
  destinationItems: T[];
  sourceIndex: number;
  destinationIndex: number;
}) {
  const [removed] = sourceItems.slice(sourceIndex, sourceIndex + 1);

  return {
    sourceItems: sourceItems.filter((item) => item.id !== removed.id),
    destinationItems: [
      ...destinationItems.slice(0, destinationIndex),
      removed,
      ...destinationItems.slice(destinationIndex),
    ],
  };
}
